import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import useApiRequest from "../../../lib/hooks/useRequest";
import { formatDate } from "../../../lib/hooks/utils";
import {
  GraphCardContentResponseDto,
  GraphCardGraphResponseDto,
  GraphCardGraphResponseDtoWithTimePeriod,
  TimePeriod,
} from "../../../lib/interfaces/bonus";
import DataFetchParent from "../../../ui/components/ui/data-fetch";
import { DatePickerWithRange } from "../../../ui/components/ui/range-date-picker";
import Graph from "../graph";
import GraphBig from "../graph-big";

type FullBonusAdminData = {
  graph: GraphCardGraphResponseDto;
  totalAmount: GraphCardContentResponseDto;
};

type FullBonusEmployeeData = {
  graph: GraphCardGraphResponseDtoWithTimePeriod;
  totalAmount: GraphCardContentResponseDto;
};

const FullBonusSmall = () => {
  const { apiRequest } = useApiRequest();
  const [date, setDate] = useState<DateRange | undefined>();
  const [dateReload, setDateReload] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FullBonusAdminData>();

  const getBonusData = useCallback(
    async (date: DateRange | undefined) => {
      setLoading(true);
      let url = "provision/admin/total-provision-amount-graph";
      if (date && date.from && date.to) {
        url += `?startDate=${date.from?.toISOString()}&endDate=${date.to?.toISOString()}`;
      }
      const response = await apiRequest<GraphCardGraphResponseDto>(url, "GET");
      const totalProvisionAmount = await apiRequest<GraphCardContentResponseDto>(
        "provision/admin/total-provision-amount",
        "GET"
      );
      if (response.data && totalProvisionAmount.data) {
        setData({
          graph: response.data,
          totalAmount: totalProvisionAmount.data,
        });
        const fromDate = new Date(response.data.startDate);
        const toDate = new Date(response.data.endDate);
        setDate({
          from: new Date(fromDate.toUTCString()),
          to: new Date(toDate.toUTCString()),
        });
        setDateReload(false);
      }
      setLoading(false);
    },
    [apiRequest]
  );

  useEffect(() => {
    getBonusData(undefined);
  }, [getBonusData]);

  useEffect(() => {
    if (dateReload) {
      getBonusData(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateReload]);

  return (
    <div className="w-1/3 bg-white rounded-lg p-4 flex flex-col gap-8">
      <div className="flex justify-between items-center">
        <h5 className="font-semibold">Gesamtbonus</h5>
        <DatePickerWithRange
          setDate={(date) => {
            setDate(date);
            if (date?.to && date?.from) {
              setDateReload(true);
            }
          }}
          date={date}
          className="w-1/2"
        />
      </div>
      <div className="flex gap-2">
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: FullBonusAdminData) => {
            return (
              <div className="flex flex-col gap-2 grow w-full">
                <div>
                  <p className="text-3xl font-semibold">{data.totalAmount.displayNumber.toLocaleString("de-DE")}€</p>
                </div>
                <div>
                  <p className="flex gap-1 text-[#888]">
                    <span
                      className={`flex ${
                        data.totalAmount.differenceLastMonth > 0
                          ? "text-[#C0DE60]"
                          : data.totalAmount.differenceLastMonth === 0 || !data.totalAmount.differenceLastMonth
                          ? "text-[#888]"
                          : "text-[#CE0909]"
                      } items-center gap-1`}
                    >
                      {data.totalAmount.differenceLastMonth > 0 ? (
                        <ArrowUp size={16} />
                      ) : data.totalAmount.differenceLastMonth === 0 || !data.totalAmount.differenceLastMonth ? (
                        <ArrowRight size={16} />
                      ) : (
                        <ArrowDown size={16} />
                      )}
                      {data.totalAmount.differenceLastMonth ?? 0}%
                    </span>
                    vs Vormonat
                  </p>
                </div>
              </div>
            );
          }}
        />
        <div className="grow w-full">
          <DataFetchParent
            isLoading={loading}
            data={data}
            renderElement={(data: FullBonusAdminData) => {
              const parsedData = data.graph.monthlyData.map((bonus) => ({
                name: `${formatDate(bonus.startDate)} - ${formatDate(bonus.endDate)}`,
                value: bonus.graphNumber ?? 0,
              }));
              return <Graph data={parsedData} />;
            }}
          />
        </div>
      </div>
    </div>
  );
};

const FullBonus = () => {
  const [dateFilter, setDateFilter] = useState<TimePeriod>(TimePeriod.LAST_12_MONTHS);
  const { apiRequest } = useApiRequest();
  const [dateReload, setDateReload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FullBonusEmployeeData>();

  const getBonusData = useCallback(async () => {
    setLoading(true);
    let url = "provision/employee/total-provision-amount-graph?timePeriod=" + dateFilter;
    const response = await apiRequest<GraphCardGraphResponseDtoWithTimePeriod>(url, "GET");
    const totalProvisionAmount = await apiRequest<GraphCardContentResponseDto>(
      "provision/employee/total-provision-amount",
      "GET"
    );
    if (response.data && totalProvisionAmount.data) {
      setData({
        graph: response.data,
        totalAmount: totalProvisionAmount.data,
      });
      setDateReload(false);
    }
    setLoading(false);
  }, [dateFilter, apiRequest]);

  useEffect(() => {
    getBonusData();
  }, [getBonusData]);

  useEffect(() => {
    if (dateReload) {
      getBonusData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateReload]);

  return (
    <div className="w-2/3 bg-white rounded-lg p-4 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <h5 className="text-[25px] font-semibold">Gesamter Bonus</h5>
          <div className="flex gap-0 bg-gray-100 rounded-lg p-1">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateFilter === TimePeriod.LAST_3_MONTHS
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setDateFilter(TimePeriod.LAST_3_MONTHS)}
            >
              3 Monate
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateFilter === TimePeriod.LAST_12_MONTHS
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setDateFilter(TimePeriod.LAST_12_MONTHS)}
            >
              12 Monate
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateFilter === TimePeriod.ALL_TIME
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setDateFilter(TimePeriod.ALL_TIME)}
            >
              Insgesamt
            </button>
          </div>
        </div>
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: FullBonusEmployeeData) => {
            return (
              <div className="flex gap-2 w-full">
                <p className="text-[25px] font-semibold">{data.totalAmount.displayNumber.toLocaleString("de-DE")}€</p>
                <div className="self-end">
                  <p className="flex gap-1 text-[#888]">
                    <span
                      className={`flex ${
                        data.totalAmount.differenceLastMonth > 0
                          ? "text-[#C0DE60]"
                          : data.totalAmount.differenceLastMonth === 0 || !data.totalAmount.differenceLastMonth
                          ? "text-[#888]"
                          : "text-[#CE0909]"
                      } items-center gap-1`}
                    >
                      {data.totalAmount.differenceLastMonth > 0 ? (
                        <ArrowUp size={16} />
                      ) : data.totalAmount.differenceLastMonth === 0 || !data.totalAmount.differenceLastMonth ? (
                        <ArrowRight size={16} />
                      ) : (
                        <ArrowDown size={16} />
                      )}
                      {data.totalAmount.differenceLastMonth ?? 0}€
                    </span>
                  </p>
                </div>
              </div>
            );
          }}
        />
      </div>
      <div className="grow w-full">
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: FullBonusEmployeeData) => {
            const parsedData = data.graph.monthlyData.map((month) => ({
              name: `${month.month}.${month.year}`,
              value: month.graphNumber ?? 0,
            }));
            return <GraphBig data={parsedData} />;
          }}
        />
      </div>
    </div>
  );
};

export { FullBonus, FullBonusSmall };
